import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import type { HttpRequestPage } from "@moment/api-collab/api-types";
import { Store as DifferentialStore } from "@moment/api-collab/differential";
import type { V1BaseSpec } from "@moment/api-collab/differential/v1-base-spec";
import { Logger } from "@moment/logging";
import { RebootQueryPersistence, queryEvaluatorStateFromReboot } from "@moment/reboot-differential";

import type { RequestPageCollabConnection } from "../request-page/request-page.types";
import { $singletonRebootContext } from "./reboot-context.atom";
import { type PageRefProps, requestPageRef } from "./refs";

const logger = new Logger("request-page-collab-connection");

/**
 * The base spec used for the request page store.
 * This is a simple schema with a single "request" table containing the HTTP request page data.
 */
export const REQUEST_PAGE_BASE_SPEC: V1BaseSpec = {
	tables: { request: { permissions: [], columns: [{ name: "request", type: "BLOB" }] } },
	views: {},
};

/**
 * The canonical Reboot connection for an HTTP request page, as identified by workspace slug,
 * document ID, branch, page ID, and SHA.
 *
 * Returns null if the application-wide singleton Reboot `WebContext` is not available.
 *
 * This atom creates and caches the DifferentialStore for request page operations.
 */
export const $requestPageCollabConnection = atomFamily((props: PageRefProps) => {
	const connectionAtom = atom(async (get): Promise<RequestPageCollabConnection | null> => {
		// Get the Reboot context
		const context = get($singletonRebootContext);
		if (!context) {
			logger.warn("RebootContext not available", { ...props });
			return null;
		}

		// Create Reboot store reference for this request page
		const rebootStoreRef = requestPageRef(props);

		// Create abort controller for the fetch
		const abortController = new AbortController();

		// Fetch initial state from Reboot
		const initialState = await queryEvaluatorStateFromReboot(
			context,
			rebootStoreRef,
			REQUEST_PAGE_BASE_SPEC,
			abortController.signal
		);

		// Create the query persistence layer
		const queryPersistence = new RebootQueryPersistence(context, rebootStoreRef, initialState);

		// Create the differential Store for request page operations
		const store = new DifferentialStore<{ request: HttpRequestPage }>(
			queryPersistence,
			{
				// Base spec for request page is fixed
				localBaseSpecReplica: () => REQUEST_PAGE_BASE_SPEC,
				saveBaseSpec: async () => {
					// Request page base spec is immutable
				},
			},
			true // isStoreOwner
		);

		logger.info("Created request page collab connection", { ...props });

		return { rebootStoreRef, store, context };
	});

	connectionAtom.debugLabel = `requestPageCollabConnection/${props.workspaceSlug}/${props.documentId}/${props.pageId}/${props.branch}/${props.sha}`;
	return connectionAtom;
}, isEqual);
